import React from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import {
  useCancelMembershipRequest,
  useJoinGroup,
  useLeaveGroup,
} from "@/api/groups";
import { Button } from "@/components/ui";
import { useModalContext } from "@/contexts/modal-context";
import { useLoggedIn } from "@/hooks";
import { GroupRoles } from "@/schemas/group-member";
import toast from "@/toast";
import { isGroupAccessRequired } from "@/utils/groups";

import type { ButtonSizes } from "@/components/ui/button/button";
import type { Group } from "@/schemas";

interface GroupActionButtonProps {
  group: Group;
  size?: ButtonSizes;
  joinGroupText?: string | React.ReactNode;
  requestAccessText?: string | React.ReactNode;
}

const messages = defineMessages({
  confirmationConfirm: {
    id: "confirmations.leave_group.confirm",
    defaultMessage: "Leave",
  },
  confirmationHeading: {
    id: "confirmations.leave_group.heading",
    defaultMessage: "Leave group",
  },
  confirmationMessage: {
    id: "confirmations.leave_group.message",
    defaultMessage:
      "You are about to leave the group. Do you want to continue?",
  },
  joinRequestSuccess: {
    id: "group.join.request_success",
    defaultMessage: "Request sent to group owner",
  },
  joinSuccess: {
    id: "group.join.success",
    defaultMessage: "Group joined successfully!",
  },
  leaveSuccess: { id: "group.leave.success", defaultMessage: "Left the group" },
});

const GroupActionButton = ({
  group,
  joinGroupText,
  requestAccessText,
  size,
}: GroupActionButtonProps) => {
  const intl = useIntl();
  const { isLoggedIn } = useLoggedIn();
  const { openModal } = useModalContext();
  const location = useLocation();
  const shouldKeepSuggestedCache = Boolean(
    location.pathname.match(/groups\/suggested/),
  );

  const joinGroup = useJoinGroup({ shouldKeepSuggestedCache });
  const leaveGroup = useLeaveGroup();
  const cancelRequest = useCancelMembershipRequest();
  const { openModal: openModalAction } = useModalContext();

  const accessRequired = isGroupAccessRequired(group);

  const isRequested = group.relationship?.requested;
  const isNonMember = !group.relationship?.member && !isRequested;
  const isOwner = group.relationship?.role === GroupRoles.OWNER;
  const isAdmin = group.relationship?.role === GroupRoles.ADMIN;
  const isBlocked = group.relationship?.blocked_by;

  const onJoinGroup = () =>
    joinGroup.mutate(
      { id: group.id },
      {
        onSuccess() {
          toast.success(
            group.locked
              ? intl.formatMessage(messages.joinRequestSuccess)
              : intl.formatMessage(messages.joinSuccess),
          );
        },
      },
    );

  const onLeaveGroup = () =>
    openModal("CONFIRM", {
      heading: intl.formatMessage(messages.confirmationHeading),
      message: intl.formatMessage(messages.confirmationMessage),
      confirm: intl.formatMessage(messages.confirmationConfirm),
      onConfirm: () =>
        leaveGroup.mutate(
          { id: group.relationship!.id },
          {
            onSuccess() {
              toast.success(intl.formatMessage(messages.leaveSuccess));
            },
          },
        ),
    });

  const onCancelRequest = () =>
    cancelRequest.mutate({
      group,
      id: group.id,
    });

  if (isBlocked || !isLoggedIn) {
    return null;
  }

  if (isOwner || isAdmin) {
    return (
      <Button
        variant="outline"
        size={size}
        onClick={() => {
          openModalAction("MANAGE_GROUP", {
            groupId: group.id,
          });
        }}
      >
        <FormattedMessage id="group.manage" defaultMessage="Manage Group" />
      </Button>
    );
  }

  if (isNonMember) {
    return (
      <Button
        variant="default"
        onClick={onJoinGroup}
        disabled={joinGroup.isLoading}
        size={size}
      >
        {accessRequired
          ? requestAccessText || (
              <FormattedMessage
                id="group.join.private"
                defaultMessage="Request Access"
              />
            )
          : joinGroupText || (
              <FormattedMessage
                id="group.join.public"
                defaultMessage="Join Group"
              />
            )}
      </Button>
    );
  }

  if (isRequested) {
    return (
      <Button
        variant="secondary"
        onClick={onCancelRequest}
        disabled={cancelRequest.isLoading}
        size={size}
      >
        <FormattedMessage
          id="group.cancel_request"
          defaultMessage="Cancel Request"
        />
      </Button>
    );
  }

  return (
    <Button
      variant="secondary"
      onClick={onLeaveGroup}
      disabled={leaveGroup.isLoading}
      size={size}
    >
      <FormattedMessage id="group.leave" defaultMessage="Leave Group" />
    </Button>
  );
};

export default GroupActionButton;
